import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { nextMsg, previousMsg, t } from '../../../common/i18n/index.js';
import { OFPageType } from '../../../generated/api/oFPageType.js';
import { type OpenFormAnswers } from '../OpenFormAnswers.js';
import { OpenFormButton } from '../OpenFormComponents/OpenFormButton.js';
import { OpenFormGridCol, OpenFormGridRow } from '../OpenFormComponents/OpenFormGrid.js';
import { OpenFormIcon } from '../OpenFormComponents/OpenFormIcon.js';
import { type OpenForm } from '../OpenFormHooks/useOpenForm.js';
import { type OpenFormGoto } from '../OpenFormHooks/useOpenFormGoto.js';
import { OpenFormPageType } from './OpenFormPage/OpenFormPageType.js';

export const OpenFormViewSection = ({
  answers,
  disabled,
  form,
  goto,
  page,
}: {
  answers: OpenFormAnswers;
  disabled: boolean;
  form: OpenForm;
  goto: OpenFormGoto;
  page: number;
}) => {
  const { setValue } = useFormContext();
  const section = useMemo(() => form.sections[page - 1], [form, page]);
  const error = answers.hasError(section, useFormContext().formState.errors);

  // Set react-hook-form value to undefined if OpenFormAnswers key is deleted
  useEffect(() => {
    form.sections.forEach(s =>
      s.questions.forEach(q => answers.isValid(s, q) || (answers.delete(q.guid) && setValue(q.guid, undefined)))
    );
  }, [answers, form, setValue]);

  return !section ? null : (
    <div id={section.guid} className="of-openform__view__section">
      <OpenFormPageType section={section} answers={answers} disabled={disabled} />
      <OpenFormGridRow className="of-openform__view__footer">
        <OpenFormGridCol className="of-openform__view__footer__content">
          <OpenFormButton
            iconPosition="left"
            icon={<OpenFormIcon icon="arrow-left" />}
            text={t.CACI(previousMsg)}
            color="link"
            size="m"
            disabled={disabled || page === 1}
            onClick={() => goto({ page: 'prev' })}
          />
          {section.pageType === OFPageType.AVAILABILITY_CHECK ? null : error ? (
            <OpenFormButton
              iconPosition="right"
              icon={<OpenFormIcon icon="arrow-right" />}
              text={t.F0MY(nextMsg)}
              color="link"
              size="m"
              disabled={disabled}
              onClick={() => document.getElementById(error.guid)?.scrollIntoView(true)}
            />
          ) : (
            <OpenFormButton
              iconPosition="right"
              icon={<OpenFormIcon icon="arrow-right" />}
              text={t.F0MY(nextMsg)}
              color="primary"
              size="m"
              disabled={disabled || !form.sections.slice(0, page).every(answers.isComplete)}
              onClick={() => goto({ page: 'next' })}
            />
          )}
        </OpenFormGridCol>
      </OpenFormGridRow>
    </div>
  );
};
